import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { pickPupHandDiscard, playPupCard, queuePupCard, stagePupCard } from '../../redux/pupApi';
import { selectPupCatalog } from '../../redux/pupCatalogSlice';
import { playCardSnap } from '../../utilities/sounds';
import { rotateDeg } from '../inGameLayout/cardFan';
import PupCard from './PupCard';
import { pupConditionMet } from './pupConditions';
import './PupHand.css';

// The only cards that get the RED "about to fire" ring + lift instead of the ordinary blue
// playable ring: the three big-combo conditional cards, and ONLY when their content
// condition is actually met (see pupConditions.js). Every other card — including the other
// conditional cards — wears the plain blue "you can play this" ring when playable.
const RED_CONDITION_CODES = ['JACKPOT', 'SEVEN_ATE_NINE', 'DEMONIC_RITUAL'];

// Movement (px) before a press counts as a drag rather than a click/hover.
const DRAG_THRESHOLD_PX = 6;

// Dock geometry — mirror of PupHand.css. One resting card is 240*0.44 px wide and the next
// tucks ~15px over its neighbour (a slight fan). Once the whole row would span more than
// half the table, every card shrinks uniformly (--pup-fit) until the row fits.
const DOCK_CARD_W = 240 * 0.44;
const CARD_OVERLAP = 15;
const MAX_ROW_FRACTION = 0.5;

// This player's Power-Up ("Pup") hand: a second hand of cards docked bottom-center of the
// table view, fanned like hole cards and peeking up from the screen edge (the bottom half
// of each resting card is clipped off screen). Hovering one raises it to full size, fully
// into view. The hand persists across rounds — a card leaves it only when PLAYED, by
// dragging it out of the dock and dropping it anywhere on the table. Each card is playable
// ONLY during its modifier's stage (and phase, for phase-gated cards: Skip wants a street's
// Start phase) while the hero is dealt in and unfolded — and never while another Power-Up
// is already resolving on the table, after a targeting timeout locked the code for the
// round, or while ANOTHER player has time frozen (freezeBlocksPlay — the freezer keeps
// their hand playable). The backend re-validates all of it.
const PupHand = ({ roomId, stage, phase, mySeat, folded, hasHoleCards, pupPlayActive,
  pupPlayMode = null, pupPlayCard = null, heroBlind = false,
  freezeBlocksPlay = false, conditionContext = null, pupDiscardActive = false }) => {
  const dispatch = useDispatch();
  // The hand is a list of card CODES (duplicates allowed); display data + play gates for
  // each resolve from the cached catalog. A code the catalog lacks renders as a placeholder
  // and stays unplayable until the self-heal refetch (inside PupCard) restores it.
  const pupCodes = useSelector((state) => state.pup.inventory[roomId]) || [];
  const lockedCodes = useSelector((state) => state.pup.locked[roomId]) || [];
  const catalog = useSelector(selectPupCatalog);
  // Index of the card whose play request is in flight (blocks double-plays); null when idle.
  const [playingIdx, setPlayingIdx] = useState(null);
  const [error, setError] = useState(null);
  // Live drag captures the CODE as well as its visual index. A private Swiper/auto-play push can
  // shrink or reorder the hand while the pointer is down; release must never re-read a different
  // card from the old index. `active` flips once movement passes the threshold.
  const [drag, setDrag] = useState(null);
  const rootRef = useRef(null);
  // Uniform shrink (<= 1) applied to the whole dock when the fanned row would otherwise
  // span more than half the table. Recomputed on card-count and container-size changes.
  const [dockFit, setDockFit] = useState(1);
  const cardCount = pupCodes.length;

  useEffect(() => {
    const el = rootRef.current;
    if (!el || cardCount === 0) return undefined;
    // The dock is centered within this table's .game-table; 50% of ITS width is the cap
    // (so split-screen sizes each dock against its own half, not the whole window).
    const container = el.closest('.game-table') || el.parentElement || document.body;
    const recompute = () => {
      const avail = (container.clientWidth || window.innerWidth) * MAX_ROW_FRACTION;
      const rowWidth = cardCount * DOCK_CARD_W - Math.max(0, cardCount - 1) * CARD_OVERLAP;
      setDockFit(rowWidth > avail && rowWidth > 0 ? avail / rowWidth : 1);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [cardCount]);

  if (mySeat == null || pupCodes.length === 0) return null;

  const cardLocked = (code) => lockedCodes.includes(code);
  // AUTOMATIC cards (the bomb pots, the Bounty button) fire on their own at the deal, and
  // TRIGGERED cards (Lifeline) fire from an engine hook — neither is EVER a manual play or
  // an auto-play-box drop (the server rejects both). They stay draggable to the STAGING
  // box (a silver automatic is legitimate exchange currency).
  const cardAutomatic = (code) => {
    const traits = catalog[code]?.traits;
    return Array.isArray(traits)
      && (traits.includes('AUTOMATIC') || traits.includes('TRIGGERED'));
  };
  // Play gates come from the catalog entry; a code without one (catalog still loading /
  // stale) is simply unplayable until the self-heal refetch lands. A null playableStage
  // means "any live betting street" (multi-stage cards like Exchange) — mirror the server
  // by accepting the four streets only. playableStage may be a CSV of stages ("flop,turn,
  // river" — the Suit Change family), and excludedPhases may bar moments on top of the
  // positive gates ("decision" = that phase of any stage; "river:end" = one stage's phase)
  // — both mirror PupPlayService's gate exactly.
  const ACTIVE_STAGES = ['preflop', 'flop', 'turn', 'river'];
  const phaseExcluded = (card) =>
    !!card.excludedPhases && !!phase && card.excludedPhases.split(',').some((token) => {
      const t = token.trim();
      const colon = t.indexOf(':');
      const exStage = colon >= 0 ? t.slice(0, colon) : null;
      const exPhase = colon >= 0 ? t.slice(colon + 1) : t;
      return (!exStage || exStage === stage) && exPhase === phase;
    });
  const cardPlayable = (code) => {
    const card = catalog[code];
    // "Dealt in" at the PRE-DEAL stage (a predeal-playable card — Flying Blind — is played
    // before any hole card exists): being unfolded is the whole gate; every other stage
    // still requires an actual hand. A foldedOnly card (Binding Contract — "cannot be
    // played if you are active") INVERTS the folded requirement: only a folded (but dealt)
    // hero may play it. A blind hero (Flying Blind) additionally loses the cards that pick
    // one of their OWN hole cards — they can't see them. All mirror PupPlayService's gates.
    const dealtIn = card && card.foldedOnly
      ? (folded && hasHoleCards)
      : (stage === 'predeal' ? !folded : (!folded && hasHoleCards));
    const blindBlocked = heroBlind
      && !!card && (card.requiresDiscard || card.cardTargetScope === 'OWN_HOLE');
    // A card on the felt freezes the table, so plays are blocked — EXCEPT an INTERRUPT card,
    // which may be played DURING the freeze (it stacks on top and resolves first). Counter
    // cards expose that same Interrupt modifier. Every other gate (stage/phase/excluded/locked/dealt-in/
    // blind) still applies; the server re-validates.
    const resolvingCard = pupPlayCard ? catalog[pupPlayCard] : null;
    const resolvingInterruptNeedsTarget = pupPlayMode === 'targeting'
      && !!resolvingCard?.isInterrupt && !!resolvingCard?.requiresTarget;
    const frozenBlocked = pupPlayActive
      && (!(card && card.isInterrupt) || resolvingInterruptNeedsTarget);
    // Player-controlled FREEZE TIME: while ANOTHER player has time frozen the hero can't
    // play ANY card — no interrupt/counter exception, the server rejects non-freezers
    // strictly. (The freezer's own dock never gets this flag.)
    // A COUNTER card (Cancel) only makes sense WHILE a play is resolving — with an empty
    // felt there is nothing to counter (the server 400s it; don't light it up).
    const counterBlocked = !!card && card.isCounter && !pupPlayActive;
    const stages = Array.isArray(card?.playableStages) ? card.playableStages : null;
    const phases = Array.isArray(card?.playablePhases) ? card.playablePhases : null;
    const stageAllowed = stages
      ? (stages.length ? stages.includes(stage) : ACTIVE_STAGES.includes(stage))
      : (card?.playableStage
        ? card.playableStage.split(',').includes(stage) : ACTIVE_STAGES.includes(stage));
    const phaseAllowed = phases
      ? (!phases.length || phases.includes(phase))
      : (!card?.playablePhase || card.playablePhase === phase);
    return !!card
      && !cardAutomatic(code)
      && stageAllowed
      && phaseAllowed
      && !phaseExcluded(card)
      && !blindBlocked
      && !cardLocked(code)
      && !frozenBlocked
      && !freezeBlocksPlay
      && !counterBlocked
      && dealtIn && playingIdx == null;
  };

  // A SILVER or GOLD card can instead be dragged into the exchange staging box to QUEUE it
  // for the next Exchange stage (3 silvers -> 1 gold, 3 golds -> 1 celestial). CELESTIAL
  // cards can't be staged: they are the TOP tier, so there is no higher colour to exchange one
  // for. The server re-validates against the card's authoritative metadata tier, and also
  // rejects staging while the Exchange stage is actively resolving.
  const cardTier = (code) => (catalog[code]?.tier || catalog[code]?.color || '').toUpperCase();
  const cardCelestial = (code) => cardTier(code) === 'CELESTIAL';
  const cardStageable = (code) => {
    const card = catalog[code];
    return !!card && ['SILVER', 'GOLD'].includes(cardTier(code))
      && stage !== 'exchange' && playingIdx == null;
  };

  // The drop target is this table's CSS-drawn rail+felt oval (PupHand renders inside the
  // same .game-table container, so `closest` always resolves the right table in split-screen).
  const isOverTable = (x, y) => {
    const table = rootRef.current?.closest('.game-table')?.querySelector('.poker-table');
    if (!table) return false;
    const r = table.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };

  // ANY card can instead be dragged into the AUTO-PLAY box, which hands it to the server to play
  // at the next moment it is legally playable. Unlike staging there is no tier restriction — every
  // tier is playable — but a code an earlier timeout round-locked is dead until the next deal, and
  // the server refuses it, so don't offer it either.
  const cardQueueable = (code) => !!catalog[code] && !cardAutomatic(code)
    && !cardLocked(code) && playingIdx == null;

  const boxRect = (selector, x, y) => {
    const box = rootRef.current?.closest('.game-table')?.querySelector(selector);
    if (!box) return false;
    const r = box.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };

  // The exchange staging box drop target (rendered as a sibling in this same .game-table).
  const isOverStagingBox = (x, y) => boxRect('.pup-staging-box', x, y);
  // ...and the auto-play box, its mirror on the left.
  const isOverAutoPlayBox = (x, y) => boxRect('.pup-autoplay-box', x, y);

  const handlePlay = async (code, idx) => {
    setPlayingIdx(idx);
    setError(null);
    try {
      await dispatch(playPupCard({ roomId, card: code })).unwrap();
      playCardSnap();
    } catch (e) {
      setError(typeof e === 'string' && e ? e : 'Could not play that card');
      setTimeout(() => setError(null), 4000);
    } finally {
      setPlayingIdx(null);
    }
  };

  const handleStage = async (code, idx) => {
    setPlayingIdx(idx);
    setError(null);
    try {
      await dispatch(stagePupCard({ roomId, card: code })).unwrap();
      playCardSnap();
    } catch (e) {
      setError(typeof e === 'string' && e ? e : 'Could not stage that card');
      setTimeout(() => setError(null), 4000);
    } finally {
      setPlayingIdx(null);
    }
  };

  const handleQueue = async (code, idx) => {
    setPlayingIdx(idx);
    setError(null);
    try {
      await dispatch(queuePupCard({ roomId, card: code })).unwrap();
      playCardSnap();
    } catch (e) {
      setError(typeof e === 'string' && e ? e : 'Could not queue that card');
      setTimeout(() => setError(null), 4000);
    } finally {
      setPlayingIdx(null);
    }
  };

  // Swiper's cost window: the dock IS the picker — one click discards the clicked card
  // (one post per window; the server locks/advances the play). No dragging meanwhile.
  const handlePupDiscard = async (code) => {
    if (playingIdx != null) return;
    setPlayingIdx(-1);
    setError(null);
    try {
      await dispatch(pickPupHandDiscard({ roomId, card: code })).unwrap();
      playCardSnap();
    } catch (e) {
      setError(typeof e === 'string' && e ? e : 'Could not discard that card');
      setTimeout(() => setError(null), 4000);
    } finally {
      setPlayingIdx(null);
    }
  };

  const onPointerDown = (e, code, idx) => {
    if (pupDiscardActive) return; // the click handler owns this window — no drags
    if (!cardPlayable(code) && !cardStageable(code) && !cardQueueable(code)) return;
    // Capture so the drag keeps tracking even when the pointer leaves the dock.
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ idx, code, x: e.clientX, y: e.clientY,
      startX: e.clientX, startY: e.clientY, active: false });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const { clientX: x, clientY: y } = e;
    setDrag((d) => d && {
      ...d, x, y,
      active: d.active || Math.hypot(x - d.startX, y - d.startY) > DRAG_THRESHOLD_PX,
    });
  };

  const onPointerUp = (e) => {
    const d = drag;
    setDrag(null);
    if (!d || !d.active) return; // a plain click — the hover zoom is the whole interaction
    // If the captured card left the authoritative hand mid-drag, just snap the ghost back. If it
    // still exists (duplicates are legitimate), use its current index for the busy indicator.
    const liveIdx = pupCodes.indexOf(d.code);
    if (liveIdx < 0) return;
    const code = d.code;
    // Either BOX wins if the release lands over it (both may sit over the felt edge): the right
    // one queues the card for exchange, the left one hands it to the server to auto-play.
    // Otherwise a release over the felt plays it now. A drop where the gesture isn't valid for
    // that target (a celestial over the exchange box, an unplayable card over the felt) just
    // snaps back — the server would reject it anyway.
    if (isOverAutoPlayBox(e.clientX, e.clientY)) {
      if (cardQueueable(code)) handleQueue(code, liveIdx);
      return;
    }
    if (isOverStagingBox(e.clientX, e.clientY)) {
      if (cardStageable(code)) handleStage(code, liveIdx);
      return;
    }
    if (isOverTable(e.clientX, e.clientY)) {
      if (cardPlayable(code)) handlePlay(code, liveIdx);
    }
    // Released elsewhere: nothing to do — the ghost unmounts and the card stays in the dock.
  };

  const draggedCode = drag?.active ? drag.code : null;
  const overAutoBox = drag?.active ? isOverAutoPlayBox(drag.x, drag.y) : false;
  const overStagingBox = drag?.active && !overAutoBox ? isOverStagingBox(drag.x, drag.y) : false;
  const overTable = drag?.active && !overStagingBox && !overAutoBox
    ? isOverTable(drag.x, drag.y) : false;
  const stageOk = overStagingBox && !!draggedCode && cardStageable(draggedCode);
  const queueOk = overAutoBox && !!draggedCode && cardQueueable(draggedCode);
  const playOk = overTable && !!draggedCode && cardPlayable(draggedCode);

  return (
    <div className="pup-hand" ref={rootRef} style={{ '--pup-fit': dockFit }}>
      {error && <div className="pup-hand-error">{error}</div>}
      <div className="pup-hand-cards">
        {pupCodes.map((code, idx) => {
          const playable = cardPlayable(code);
          const locked = cardLocked(code);
          const beingDragged = drag?.active && drag.idx === idx && drag.code === code;
          const card = catalog[code];
          // The RED "about to fire" ring + lift is reserved for two cases:
          //   1. Jackpot / Seven Ate Nine / Demonic Ritual, and ONLY when their description's
          //      content condition is actually met (three 7s, a 7-8-9, three 6s).
          //   2. An INTERRUPT card (incl. counters) that can legally land RIGHT NOW on top of
          //      another Power-Up already resolving on the felt.
          // These three codes also SUPPRESS the blue ring when their condition ISN'T met (the
          // blue "you can play this" promise would be a lie — the server would 400 the play).
          const redConditional = RED_CONDITION_CODES.includes(code);
          const conditionArmed = redConditional && playable && !!conditionContext
            && pupConditionMet(code, conditionContext);
          const interruptArmed = playable && !!card?.isInterrupt && pupPlayActive;
          const armed = conditionArmed || interruptArmed;
          const automatic = cardAutomatic(code);
          return (
            <div
              key={`${code}-${idx}`}
              className={
                'pup-hand-card'
                + (playable ? ' pup-hand-card-playable' : '')
                + (redConditional ? ' pup-hand-card-conditional' : '')
                + (armed ? ' pup-hand-card-armed' : '')
                + (locked ? ' pup-hand-card-locked' : '')
                + (automatic ? ' pup-hand-card-auto' : '')
                + (pupDiscardActive ? ' pup-hand-card-discardable' : '')
                + (playingIdx === idx ? ' pup-hand-card-playing' : '')
                + (beingDragged ? ' pup-hand-card-dragged' : '')
              }
              // Fan tilt: same -4°..+4° linear spread as hole cards (cardFan.rotateDeg).
              // Set as a CSS var so the hover rule can straighten the card to 0°.
              style={{ '--pup-tilt': `${rotateDeg(idx, pupCodes.length)}deg` }}
              onPointerDown={(e) => onPointerDown(e, code, idx)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={() => setDrag(null)}
              onClick={pupDiscardActive ? () => handlePupDiscard(code) : undefined}
              onDoubleClick={() => !pupDiscardActive && cardPlayable(code) && handlePlay(code, idx)}
            >
              <div className="pup-hand-scale">
                <PupCard code={code} roomId={roomId} />
                {/* Fires on its own (deal sweep / engine trigger) — never a manual play. */}
                {automatic && <div className="pup-auto-tag">AUTO</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Drag ghost: the card riding the pointer, above everything. pointer-events: none
          so it never swallows the pointer stream captured by the dock wrapper. Portaled to
          <body>: .pup-hand carries a CSS transform, which would otherwise hijack the
          ghost's position:fixed and anchor it to the dock instead of the viewport. */}
      {drag?.active && drag.code && createPortal(
        <div
          className={`pup-drag-ghost${(stageOk || queueOk || playOk) ? ' pup-drag-ghost-over' : ''}`}
          style={{ left: drag.x, top: drag.y }}
        >
          <PupCard code={drag.code} roomId={roomId} />
          <div className="pup-drag-label">{
            overAutoBox ? (queueOk ? 'Release to auto-play'
              : (cardAutomatic(draggedCode) ? 'Plays automatically' : 'Locked for this round'))
              : overStagingBox ? (stageOk ? 'Release to stage'
                : (cardCelestial(draggedCode) ? 'Celestial is the top tier'
                  : 'Only silver or gold cards'))
                : overTable ? (playOk ? 'Release to play' : "Can't play that here")
                  : 'Table = play · Left = auto · Right = exchange'
          }</div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PupHand;
